import { useLayoutEffect, useRef } from "react";

/**
 * Auto-shrinks its child text until it fits within the container's max-height.
 * The container is the rendered <div> itself; size it with `style.maxHeight`
 * (px or %) or via className. The hook shrinks font-size only — never word-clips.
 *
 * Works with parent resizes via ResizeObserver. Resets to the CSS-default size
 * each pass so growing the container also grows the text back to its natural size.
 */
export default function FitText({ text, as: Tag = "div", className = "", style, minPx = 8 }) {
  const ref = useRef(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !text) return;

    const shrink = () => {
      // Reset to className-defined size before each measurement.
      el.style.fontSize = "";
      // If the element hasn't been laid out yet, skip — ResizeObserver will retry.
      if (el.clientHeight <= 0 || el.clientWidth <= 0) return;
      const computed = window.getComputedStyle(el);
      let size = parseFloat(computed.fontSize);
      // Don't let runaway loops happen.
      let safety = 40;
      while (
        (el.scrollHeight > el.clientHeight + 1 || el.scrollWidth > el.clientWidth + 1) &&
        size > minPx &&
        safety-- > 0
      ) {
        size = Math.max(minPx, size * 0.92);
        el.style.fontSize = `${size}px`;
      }
    };

    shrink();
    const ro = new ResizeObserver(shrink);
    ro.observe(el);
    if (el.parentElement) ro.observe(el.parentElement);
    return () => ro.disconnect();
  }, [text, minPx]);

  return (
    <Tag
      ref={ref}
      className={className}
      style={{ ...style, overflow: "hidden", wordBreak: "break-word" }}
    >
      {text}
    </Tag>
  );
}
